import { nextActionCardWakeAt } from '@littlebird/action-cards';
import { useEffect, useState } from 'react';

import type { ActionCard } from '@littlebird/sdk';

// setTimeout fires at once past this delay, about 24.8 days
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

/**
 * The time to hide snoozed cards against. It moves on when the earliest snoozed card is due back,
 * and when the window comes back into view, since timers fall behind while the computer sleeps.
 */
export const useSnoozeClock = (cards: readonly ActionCard[]): number => {
  // React Compiler would cache the wake time on the array and miss a card snoozed in place
  'use no memo';
  const [nowMs, setNowMs] = useState(() => Date.now());
  const wakeAtMs = nextActionCardWakeAt(cards, nowMs);

  // Set again after each catch-up, since a timer that ran during sleep has fallen behind the clock
  useEffect(() => {
    if (wakeAtMs === null) return;
    const timer = setTimeout(
      () => setNowMs(Date.now()),
      Math.min(Math.max(0, wakeAtMs - Date.now()), MAX_TIMER_DELAY_MS)
    );
    return () => clearTimeout(timer);
  }, [wakeAtMs, nowMs]);

  useEffect(() => {
    if (wakeAtMs === null) return;
    const catchUp = () => {
      if (document.visibilityState === 'visible') setNowMs(Date.now());
    };
    document.addEventListener('visibilitychange', catchUp);
    window.addEventListener('focus', catchUp);
    return () => {
      document.removeEventListener('visibilitychange', catchUp);
      window.removeEventListener('focus', catchUp);
    };
  }, [wakeAtMs]);

  return nowMs;
};
